import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseAPI, passwordAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiPhone, FiSearch, FiKey, FiEye, FiEyeOff, FiCopy, FiPlus, FiEdit2 } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const Students = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search filters
  const [filters, setFilters] = useState({
    search: ''
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    from: null,
    to: null
  });
  
  // Password reset modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStudents(1);
    } else {
      setLoading(false);
      navigate('/login');
    }
  }, [isAuthenticated]);

  const fetchStudents = async (page = 1, searchFilters = {}) => {
    if (!isAuthenticated) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: page,
        per_page: 20,
        ...searchFilters
      };
      
      const response = await courseAPI.getStudents(params);
      if (response.data.success) {
        setStudents(response.data.data);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || 'Error fetching students');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStudents(1, filters);
  };
  
  const handleOpenResetModal = (student) => {
    setSelectedStudent(student);
    setNewPassword('');
    setShowPassword(false);
    setResetSuccess(false);
    setResetResult(null);
    setShowResetModal(true);
  };
  
  const generatePassword = () => {
    const length = 8;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setNewPassword(password);
    setShowPassword(true);
  };
  
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to reset password for ${selectedStudent.name}?`)) {
      return;
    }
    
    setResetting(true);
    try {
      const response = await passwordAPI.resetStudentPassword(selectedStudent.id, {
        new_password: newPassword
      });
      
      if (response.data.success) {
        toast.success('Password reset successfully');
        setResetSuccess(true);
        setResetResult(response.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error resetting password');
    } finally {
      setResetting(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Password copied to clipboard!');
  };
  
  const closeResetModal = () => {
    setShowResetModal(false);
    setSelectedStudent(null);
    setNewPassword('');
    setResetSuccess(false);
    setResetResult(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Students</h1>
          <p className="text-sm md:text-base text-gray-600">Manage students enrolled in your courses</p>
        </div>
        <button
          onClick={() => navigate('/students/add')}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors w-full sm:w-auto justify-center"
        >
          <FiPlus /> Add New Student
        </button>
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-4 md:mb-6">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiSearch /> Search Students
        </h3>
        
        <div className="grid grid-cols-1 gap-4">
          {/* Keyword Search */}
          <div>
            <form onSubmit={handleSearch}>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Search By Keyword
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Search by Name, Email, Contact..."
                  className="flex-1 px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none text-sm md:text-base"
                />
                <button
                  type="submit"
                  className="px-4 md:px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] flex items-center justify-center"
                >
                  <FiSearch className="md:mr-2" />
                  <span className="hidden md:inline">Search</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        {filters.search && (
          <button
            onClick={() => {
              setFilters({ search: '' });
              fetchStudents(1);
            }}
            className="mt-4 text-xs md:text-sm text-primary-600 hover:text-primary-700 min-h-[44px] flex items-center"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Table View */}
      {students.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 md:py-16 bg-gray-50 rounded-lg">
          <FiUser size={48} className="md:w-16 md:h-16 text-gray-300 mb-4" />
          <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">No students found</h3>
          <p className="text-sm md:text-base text-gray-500 mb-6">No students have purchased courses yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Student ID</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Class</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Batch</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Joined Date</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-900">{student.id}</td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="w-9 h-9 md:w-10 md:h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                          {student.firstname?.charAt(0) || 'S'}
                        </div>
                        <div>
                          <p className="text-xs md:text-sm font-medium text-gray-900">
                            {student.firstname} {student.lastname || ''}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                        <FiMail size={14} />
                        {student.email}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                        <FiPhone size={14} />
                        {student.mobileno || '-'}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        student.class_name 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {student.class_name || 'Not Assigned'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        student.batch_name 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {student.batch_name || 'Not Assigned'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">
                      {student.cdate ? new Date(student.cdate).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        student.status == 0
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {student.status == 0 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/students/edit/${student.id}`)}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs rounded transition-colors"
                          title="Edit Student"
                        >
                          <FiEdit2 size={14} /> Edit
                        </button>
                        <button
                          onClick={() => handleOpenResetModal(student)}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 text-xs rounded transition-colors"
                          title="Reset Password"
                        >
                          <FiKey size={14} /> Reset Password
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && students.length > 0 && pagination.last_page > 1 && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-700">
              Showing {pagination.from} to {pagination.to} of {pagination.total} students
            </div>
            
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {/* First Page */}
              <button
                onClick={() => fetchStudents(1, filters)}
                disabled={pagination.current_page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              
              {/* Previous Page */}
              <button
                onClick={() => fetchStudents(pagination.current_page - 1, filters)}
                disabled={pagination.current_page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex gap-1">
                {[...Array(pagination.last_page)].map((_, index) => {
                  const pageNum = index + 1;
                  const currentPage = pagination.current_page;
                  
                  // Show first, last, current and 2 pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.last_page ||
                    (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => fetchStudents(pageNum, filters)}
                        className={`px-3 py-1 text-sm border rounded transition-colors ${
                          pageNum === currentPage
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 3 ||
                    pageNum === currentPage + 3
                  ) {
                    return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>
              
              {/* Next Page */}
              <button
                onClick={() => fetchStudents(pagination.current_page + 1, filters)}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
              
              {/* Last Page */}
              <button
                onClick={() => fetchStudents(pagination.last_page, filters)}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Reset Student Password</h3>
            </div>
            
            <div className="p-6">
              {!resetSuccess ? (
                <>
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Student:</strong> {selectedStudent?.firstname} {selectedStudent?.lastname}
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Email:</strong> {selectedStudent?.email}
                    </p>
                  </div>
                  
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800">
                      <strong>Note:</strong> This will reset the student's password without requiring their current password. Use this when a student has forgotten their password.
                    </p>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                        placeholder="Enter new password (min 6 characters)"
                        minLength="6"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="w-full mb-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors"
                  >
                    Generate Random Password
                  </button>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={closeResetModal}
                      className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                      disabled={resetting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetPassword}
                      className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={resetting || !newPassword}
                    >
                      {resetting ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-medium mb-2">Password Reset Successfully!</p>
                    <p className="text-sm text-gray-700 mb-3">
                      The password has been reset for <strong>{resetResult?.student_name}</strong>
                    </p>
                    
                    <div className="bg-white border border-gray-300 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">New Password:</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm font-mono bg-gray-50 px-3 py-2 rounded border border-gray-200">
                          {resetResult?.new_password}
                        </code>
                        <button
                          onClick={() => copyToClipboard(resetResult?.new_password)}
                          className="p-2 bg-primary-100 hover:bg-primary-200 text-primary-700 rounded transition-colors"
                          title="Copy to clipboard"
                        >
                          <FiCopy size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-600 mt-3">
                      Please share this password with the student securely.
                    </p>
                  </div>
                  
                  <button
                    onClick={closeResetModal}
                    className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
